<template>
  <div v-if="templates?.length" class="mt-3 form-group">
    <div v-for="number in numbers" :key="`owa-number-${number.branchId}`">
      <SelectGroup :model-value="selectedIds[number.branchId] || ''"
                   @update:modelValue="select(number.branchId, $event)"
                   :options="number.options"
                   :label="numbers.length > 1 ? `${$t('official_whatsapp_template')}: ${number.title}` : $t('official_whatsapp_template')"
                   :showlabel="true"
                   :translate="false"
                   :null-option="true"
                   variant="white"/>
      <div v-if="headerOf(number.branchId)" class="small text-muted mt-n2 mb-3">{{ $t('owa_template_with_header_hint', { header: $t(`owa_header_${headerOf(number.branchId)}`) }) }}</div>
    </div>
    <ValidationError validation-id="official_whatsapp_template_ids"/>
    <div class="small text-muted">{{ $t(copyText ? 'owa_template_select_hint' : 'owa_birthday_template_select_hint') }}</div>
    <div v-if="numbers.length > 1" class="small text-muted">{{ $t('owa_template_per_number_hint') }}</div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, watch } from "vue"
import { useStore } from "vuex"
import SelectGroup from "@/components/form/SelectGroup.vue"
import ValidationError from "@/components/form/ValidationError.vue"

// approved official whatsapp templates come only for numbers connected through Aiva. A template is made in one number (its branch_id),
// so one is chosen for every number: a branch sends the one of the number it uses (its own or the one of another branch)
const props = defineProps({
  object: {
    type: Object
  },
  templates: {
    type: Array,
    default: () => []
  },
  // notification settings and mailings keep the template text as their text (it goes to the notifications log); birthday greetings keep their own sms text
  copyText: {
    type: Boolean,
    default: true
  },
})

const store = useStore()

const branches = computed(() => store.state.auth.clinic?.branches || [])

// numbers that have templates for this form, in the order of branches
const numbers = computed(() => {
  const branchIds = [...new Set((props.templates || []).map(t => Number(t.branch_id)))]
  return branchIds
      .map(branchId => ({
        branchId,
        title: branches.value.find(b => Number(b.id) === branchId)?.title || '',
        options: props.templates.filter(t => Number(t.branch_id) === branchId).map(t => ({ ...t, id: String(t.id) })),
      }))
      .sort((a, b) => a.branchId - b.branchId)
})

const ids = () => (props.object.official_whatsapp_template_ids || []).map(Number)

// branch_id of the number -> id of the chosen template (as a string, the select gives strings)
const selectedIds = computed(() => {
  const res = {}
  ids().forEach(id => {
    const template = (props.templates || []).find(t => Number(t.id) === id)
    if(template) res[Number(template.branch_id)] = String(id)
  })
  return res
})

const select = (branchId, id) => {
  const others = ids().filter(existing => {
    const template = (props.templates || []).find(t => Number(t.id) === existing)
    return template && Number(template.branch_id) !== branchId
  })
  props.object.official_whatsapp_template_ids = id ? [...others, Number(id)] : others
}

// template with a media header takes the attachment of the notification / the image of the mailing, without it the message is not sent
const headerOf = (branchId) => {
  const id = Number(selectedIds.value[branchId])
  return (props.templates || []).find(t => Number(t.id) === id)?.header || null
}

onBeforeMount(() => {
  if(!Array.isArray(props.object.official_whatsapp_template_ids)) props.object.official_whatsapp_template_ids = []
})

// the list depends on the form (notification type, channel): templates that are not in it anymore are not saved
watch(() => props.templates, (templates) => {
  const available = (templates || []).map(t => Number(t.id))
  const kept = ids().filter(id => available.includes(id))
  if(kept.length !== ids().length) props.object.official_whatsapp_template_ids = kept
}) // not immediate: the list may still be loading when the form opens

// the text of the template of the first number is the text of the setting / mailing
watch(() => ids().join(','), () => {
  if(!props.copyText) return
  const first = numbers.value.map(n => Number(selectedIds.value[n.branchId])).find(Boolean)
  const template = (props.templates || []).find(t => Number(t.id) === first)
  if(template) props.object.text = template.text
})
</script>
